import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  gaUserDashboardLoad,
  gaTabSwitch,
  gaAWBSearch,
  gaDateFilter,
  gaCountryFilter,
  gaCustomerFilter,
  gaShipmentView,
  gaDownloadAWBCopy,
  gaDownloadLEO,
  gaDownloadINV,
  gaDownloadInvoice,
  gaInsightsOpen,
  gaShipmentsLoaded,
} from '../../utils/analytics';
import UserShell from '../Layout/UserShell';
import UserStatCards from './UserStatCards';
import ShipmentCard from './ShipmentCard';
import { Download, Package, Calendar, Plane, Ship, Upload, ChevronDown, ChevronRight, X, BarChart3, CreditCard, Wallet, CheckCircle, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { API_BASE_URL, API_ENDPOINTS, apiCall } from '../../config/api';
import { getCloudFrontUrl } from '../../utils/s3Upload';
import trackingProviders from '../../config/trackingProviders.json';
import AwbTrackingModal from './AwbTrackingModal';
import FobCalendarModal from './FobCalendarModal';
import { DateRangePicker } from 'react-date-range';
import { escapeCsv } from '../../utils/exportCsv';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_Sr7K03JCPKNw2c';

// AWB prefixes with live tracking via track.quickglobal.in — everything else
// keeps going straight to the carrier's own tracking site. This is a fallback
// used until /health has actually answered (or if it doesn't).
const FALLBACK_LIVE_TRACKING_PREFIXES = new Set(['020', '057', '071', '074', '217', '232', '312', '607', '615', '807']);
const TRACKING_HEALTH_URL = 'https://track.quickglobal.in/health';

// ─── AWB Tracking via direct airline URL ────────────────────────────────────
const handleTrackAWB = async (awbNo) => {
  if (!awbNo) { toast.error('AWB number not available for tracking.'); return; }

  const digits = String(awbNo).replace(/[^0-9]/g, '');
  if (digits.length < 4) { toast.error('Invalid AWB number.'); return; }

  const prefix = digits.slice(0, 3);
  const awb8   = digits.slice(3);

  const provider = trackingProviders.find(p => p.prefix === prefix);
  if (!provider) { toast('Tracking not available for this airline.', { icon: '✈️' }); return; }

  // Resolve param placeholders
  const resolve = (val) =>
    val.replace('{prefix}', prefix).replace('{awb8}', awb8).replace('{fullAwb}', digits);

  // Copy per-provider format to clipboard (defaults to 8-digit)
  const clipText = resolve(provider.copy || '{awb8}');
  try { await navigator.clipboard.writeText(clipText); toast.success(`Copied: ${clipText}`); } catch { /* ignore */ }

  const method = (provider.method || 'get').toLowerCase();
  const resolvedParams = Object.fromEntries(
    Object.entries(provider.params || {}).map(([k, v]) => [k, resolve(v)])
  );

  if (method === 'get') {
    const qs  = new URLSearchParams(resolvedParams).toString();
    const url = `${resolve(provider.url)}${qs ? '?' + qs : ''}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    // POST — build hidden form and submit to new tab
    const form = document.createElement('form');
    form.action = resolve(provider.url);
    form.method = 'POST';
    form.target = '_blank';
    form.style.display = 'none';
    Object.entries(resolvedParams).forEach(([name, value]) => {
      const inp = document.createElement('input');
      inp.type = 'hidden'; inp.name = name; inp.value = value;
      form.appendChild(inp);
    });
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }
};
// ─────────────────────────────────────────────────────────────────────────────

// Comprehensive country name → ISO 3166-1 alpha-2 code mapping
const COUNTRY_ISO = {
  'afghanistan':'AF','albania':'AL','algeria':'DZ','andorra':'AD','angola':'AO',
  'antigua and barbuda':'AG','argentina':'AR','armenia':'AM','australia':'AU','austria':'AT',
  'azerbaijan':'AZ','bahamas':'BS','bahrain':'BH','bangladesh':'BD','barbados':'BB',
  'belarus':'BY','belgium':'BE','belize':'BZ','benin':'BJ','bhutan':'BT',
  'bolivia':'BO','bosnia and herzegovina':'BA','botswana':'BW','brazil':'BR','brunei':'BN',
  'bulgaria':'BG','burkina faso':'BF','burundi':'BI','cabo verde':'CV','cambodia':'KH',
  'cameroon':'CM','canada':'CA','central african republic':'CF','chad':'TD','chile':'CL',
  'china':'CN','colombia':'CO','comoros':'KM','congo':'CG','democratic republic of the congo':'CD',
  'costa rica':'CR','croatia':'HR','cuba':'CU','cyprus':'CY','czech republic':'CZ',
  'czechia':'CZ','denmark':'DK','djibouti':'DJ','dominica':'DM','dominican republic':'DO',
  'ecuador':'EC','egypt':'EG','el salvador':'SV','equatorial guinea':'GQ','eritrea':'ER',
  'estonia':'EE','eswatini':'SZ','ethiopia':'ET','fiji':'FJ','finland':'FI',
  'france':'FR','gabon':'GA','gambia':'GM','georgia':'GE','germany':'DE',
  'ghana':'GH','greece':'GR','grenada':'GD','guatemala':'GT','guinea':'GN',
  'guinea-bissau':'GW','guyana':'GY','haiti':'HT','honduras':'HN','hong kong':'HK',
  'hungary':'HU','iceland':'IS','india':'IN','indonesia':'ID','iran':'IR',
  'iraq':'IQ','ireland':'IE','israel':'IL','italy':'IT','ivory coast':'CI',
  'cote d ivoire':'CI','jamaica':'JM','japan':'JP','jordan':'JO','kazakhstan':'KZ',
  'kenya':'KE','kiribati':'KI','north korea':'KP','south korea':'KR','korea':'KR',
  'kosovo':'XK','kuwait':'KW','kyrgyzstan':'KG','laos':'LA','latvia':'LV',
  'lebanon':'LB','lesotho':'LS','liberia':'LR','libya':'LY','liechtenstein':'LI',
  'lithuania':'LT','luxembourg':'LU','madagascar':'MG','malawi':'MW','malaysia':'MY',
  'maldives':'MV','mali':'ML','malta':'MT','marshall islands':'MH','mauritania':'MR',
  'mauritius':'MU','mexico':'MX','micronesia':'FM','moldova':'MD','monaco':'MC',
  'mongolia':'MN','montenegro':'ME','morocco':'MA','mozambique':'MZ','myanmar':'MM',
  'namibia':'NA','nauru':'NR','nepal':'NP','netherlands':'NL','new zealand':'NZ',
  'nicaragua':'NI','niger':'NE','nigeria':'NG','north macedonia':'MK','norway':'NO',
  'oman':'OM','pakistan':'PK','palau':'PW','panama':'PA','papua new guinea':'PG',
  'paraguay':'PY','peru':'PE','philippines':'PH','poland':'PL','portugal':'PT',
  'qatar':'QA','romania':'RO','russia':'RU','rwanda':'RW','saint kitts and nevis':'KN',
  'saint lucia':'LC','saint vincent and the grenadines':'VC','samoa':'WS','san marino':'SM',
  'sao tome and principe':'ST','saudi arabia':'SA','senegal':'SN','serbia':'RS',
  'seychelles':'SC','sierra leone':'SL','singapore':'SG','slovakia':'SK','slovenia':'SI',
  'solomon islands':'SB','somalia':'SO','south africa':'ZA','south sudan':'SS','spain':'ES',
  'sri lanka':'LK','sudan':'SD','suriname':'SR','sweden':'SE','switzerland':'CH',
  'syria':'SY','taiwan':'TW','tajikistan':'TJ','tanzania':'TZ','thailand':'TH',
  'timor-leste':'TL','togo':'TG','tonga':'TO','trinidad and tobago':'TT','tunisia':'TN',
  'turkey':'TR','turkmenistan':'TM','tuvalu':'TV','uganda':'UG','ukraine':'UA',
  'uae':'AE','united arab emirates':'AE','dubai':'AE','abu dhabi':'AE',
  'uk':'GB','united kingdom':'GB','great britain':'GB',
  'usa':'US','united states':'US','united states of america':'US','u.s.a.':'US','u.s.':'US',
  'uruguay':'UY','uzbekistan':'UZ','vanuatu':'VU','vatican city':'VA',
  'venezuela':'VE','vietnam':'VN','viet nam':'VN','yemen':'YE','zambia':'ZM','zimbabwe':'ZW',
};

// Convert ISO code to flag emoji using Unicode Regional Indicator letters
const isoToFlag = (iso) => {
  if (!iso || iso.length !== 2) return '🌍';
  return [...iso.toUpperCase()].map(c => String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))).join('');
};

const countryToFlag = (countryName) => {
  if (!countryName) return '🌍';
  const key = countryName.toLowerCase().trim();
  const iso = COUNTRY_ISO[key];
  return iso ? isoToFlag(iso) : '🌍';
};


// Country name to code mapping
const countryToCode = (countryName) => {
  if (!countryName) return '';
  const key = countryName.toLowerCase().trim();
  return COUNTRY_ISO[key] || countryName.substring(0, 2).toUpperCase();
};

const getCurrencySymbol = (currency) => {
  if (!currency) return '';
  const code = String(currency).trim().toUpperCase();
  const map = {
    INR: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
    AED: 'د.إ',
    SAR: '﷼',
    JPY: '¥',
    CNY: '¥',
    HKD: 'HK$',
    SGD: 'S$',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'CHF'
  };
  return map[code] || code;
};

const formatFcValueWithCurrency = (fcValue, fcType) => {
  if (!fcValue || fcValue === '-') return '-';
  const symbol = getCurrencySymbol(fcType);
  const rawValue = String(fcValue).trim();

  // Avoid double prefix when value already starts with same symbol/code
  if (symbol && (rawValue.startsWith(symbol) || rawValue.toUpperCase().startsWith(String(fcType || '').toUpperCase()))) {
    return rawValue;
  }

  return symbol ? `${symbol}${rawValue}` : rawValue;
};

const normalizeSeaContainerNoDisplay = (value) => {
  if (value === null || value === undefined) return '';

  const isValid = (v) => {
    const t = String(v || '').trim();
    return t !== '' && t !== '-';
  };

  if (Array.isArray(value)) {
    const cleaned = value.map(v => String(v || '').trim()).filter(isValid);
    return cleaned.join(' & ');
  }

  const raw = String(value).trim();
  if (!raw || raw === '-' || raw === '[]' || raw === '[""]') return '';

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map(v => String(v || '').trim()).filter(isValid).join(' & ');
    }
  } catch {
    // fallback below
  }

  if (raw.includes(' & ') || raw.includes(',')) {
    return raw
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(isValid)
      .join(' & ');
  }

  return isValid(raw) ? raw : '';
};

// ─── Wallet Payment Modal ────────────────────────────────────────────────────
const WalletPayModal = ({ totalAmount, walletBalance, onClose, onPayWallet, onPaySplit, onPayRazorpay }) => {
  const maxWallet = Math.min(walletBalance, totalAmount);
  const [walletAmt, setWalletAmt] = React.useState(String(maxWallet));
  const [err, setErr] = React.useState('');

  const fmt = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
  const parsed = parseFloat(walletAmt) || 0;
  const cardAmt = Math.max(0, totalAmount - parsed);

  const handlePay = () => {
    if (!parsed || parsed <= 0) { setErr('Please enter a valid amount'); return; }
    if (parsed > walletBalance) { setErr(`Your wallet only has ${fmt(walletBalance)}`); return; }
    if (parsed > totalAmount) { setErr(`Cannot exceed invoice amount ${fmt(totalAmount)}`); return; }
    setErr('');
    cardAmt === 0 ? onPayWallet() : onPaySplit(parsed);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 w-full max-w-sm" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">Invoice Amount</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{fmt(totalAmount)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* Wallet balance row */}
          <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800/60">
            <div className="flex items-center gap-2">
              <Wallet size={15} className="text-emerald-500" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Wallet Balance</span>
            </div>
            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmt(walletBalance)}</span>
          </div>

          {/* Wallet amount input */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
              How much to pay from wallet?
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">₹</span>
              <input
                type="number"
                min="1"
                max={maxWallet}
                value={walletAmt}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '' || val === '-') { setWalletAmt(val); setErr(''); return; }
                  const num = parseFloat(val);
                  if (!isNaN(num) && num > maxWallet) { setWalletAmt(String(maxWallet)); setErr(`Max wallet amount is ${fmt(maxWallet)}`); return; }
                  setWalletAmt(val); setErr('');
                }}
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-semibold focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-500 transition-colors"
              />
            </div>
            {err
              ? <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">⚠ {err}</p>
              : <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">Max usable: {fmt(maxWallet)}</p>
            }
          </div>

          {/* Payment breakdown */}
          {parsed > 0 && (
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50">
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                  <Wallet size={12} className="text-emerald-500" /> From Wallet
                </span>
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{fmt(parsed)}</span>
              </div>
              {cardAmt > 0 && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <CreditCard size={12} className="text-blue-500" /> Via Card / UPI
                  </span>
                  <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">{fmt(cardAmt)}</span>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Total</span>
                <span className="text-xs font-bold text-gray-900 dark:text-white">{fmt(totalAmount)}</span>
              </div>
            </div>
          )}

          {/* Main pay button */}
          <button
            onClick={handlePay}
            disabled={!parsed || parsed <= 0}
            className="w-full py-3.5 rounded-xl font-bold text-sm text-white shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] disabled:opacity-40 disabled:scale-100 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 flex items-center justify-center gap-2"
          >
            <Wallet size={16} />
            {cardAmt > 0
              ? `Pay ${fmt(parsed)} Wallet + ${fmt(cardAmt)} Card`
              : `Pay ${fmt(parsed)} from Wallet`
            }
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
            <span className="text-[11px] text-gray-400 dark:text-gray-500">or pay full via</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
          </div>

          {/* Full card pay */}
          <button
            onClick={onPayRazorpay}
            className="w-full py-3 rounded-xl font-semibold text-sm border-2 border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all flex items-center justify-center gap-2"
          >
            <CreditCard size={15} className="text-blue-500" />
            Card / UPI &nbsp;—&nbsp; {fmt(totalAmount)}
          </button>

        </div>
      </div>
    </div>
  );
};

// Toolbar buttons: one filled primary, the rest tinted outlines.
// Paying is the one action that still reads better as a button: it turns up
// beside a selection, and in the bar that follows the page down.
const PAY_BTN = 'inline-flex items-center gap-2 h-11 px-4 rounded-xl text-[13px] font-bold '
  + 'border border-emerald-700/30 text-white bg-gradient-to-b from-emerald-500 to-emerald-600 '
  + 'shadow-md shadow-emerald-600/25 transition hover:to-emerald-700 hover:-translate-y-px active:translate-y-0';

// The four things you can do from here are cards rather than pills: the icon
// gets a tile of its own, the label gets a line saying what it does, and an
// arrow marks it as somewhere to go. Each carries one accent, and the primary
// action is the only filled one.
const CARD_TONES = {
  blue: {
    shell: 'border-blue-200 from-white to-blue-50/80 hover:border-blue-300 hover:shadow-blue-500/15 '
         + 'dark:border-blue-500/25 dark:from-blue-500/[0.06] dark:to-blue-500/[0.14] '
         + 'dark:hover:border-blue-500/45 dark:hover:shadow-blue-500/10',
    tile:  'from-blue-500 to-blue-600 shadow-blue-600/30',
    title: 'text-blue-900 dark:text-blue-100',
    note:  'text-blue-700/70 dark:text-blue-300/70',
    arrow: 'bg-blue-100 text-blue-700 group-hover:bg-blue-600 group-hover:text-white '
         + 'dark:bg-blue-500/20 dark:text-blue-300',
    glow:  'bg-blue-500/20',
  },
  emerald: {
    shell: 'border-emerald-200 from-white to-emerald-50/80 hover:border-emerald-300 hover:shadow-emerald-500/15 '
         + 'dark:border-emerald-500/25 dark:from-emerald-500/[0.06] dark:to-emerald-500/[0.14] '
         + 'dark:hover:border-emerald-500/45 dark:hover:shadow-emerald-500/10',
    tile:  'from-emerald-500 to-emerald-600 shadow-emerald-600/30',
    title: 'text-emerald-900 dark:text-emerald-100',
    note:  'text-emerald-700/70 dark:text-emerald-300/70',
    arrow: 'bg-emerald-100 text-emerald-700 group-hover:bg-emerald-600 group-hover:text-white '
         + 'dark:bg-emerald-500/20 dark:text-emerald-300',
    glow:  'bg-emerald-500/20',
  },
  violet: {
    shell: 'border-violet-200 from-white to-violet-50/80 hover:border-violet-300 hover:shadow-violet-500/15 '
         + 'dark:border-violet-500/25 dark:from-violet-500/[0.06] dark:to-violet-500/[0.14] '
         + 'dark:hover:border-violet-500/45 dark:hover:shadow-violet-500/10',
    tile:  'from-violet-500 to-violet-600 shadow-violet-600/30',
    title: 'text-violet-900 dark:text-violet-100',
    note:  'text-violet-700/70 dark:text-violet-300/70',
    arrow: 'bg-violet-100 text-violet-700 group-hover:bg-violet-600 group-hover:text-white '
         + 'dark:bg-violet-500/20 dark:text-violet-300',
    glow:  'bg-violet-500/20',
  },
};

const ActionCard = ({ icon: Icon, title, note, tone = 'blue', filled = false, onClick }) => {
  const t = CARD_TONES[tone] || CARD_TONES.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative overflow-hidden w-full flex items-center gap-3.5 px-3.5 py-3 rounded-2xl
                  border text-left transition duration-150 ease-out shadow-sm
                  hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-sm
                  ${filled
                    ? 'border-blue-700/30 bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-600/25 hover:shadow-blue-600/35'
                    : `bg-gradient-to-br ${t.shell}`}`}
    >
      {/* a soft bloom in the corner, the way the tile would cast one */}
      <span aria-hidden="true"
        className={`pointer-events-none absolute -right-8 -top-10 w-28 h-28 rounded-full blur-2xl
                    ${filled ? 'bg-white/20' : t.glow}`} />

      <span className={`relative grid place-items-center w-11 h-11 rounded-xl flex-shrink-0 shadow-md
                        ${filled ? 'bg-white/20 text-white shadow-blue-900/20' : `bg-gradient-to-br text-white ${t.tile}`}`}>
        <Icon size={20} />
      </span>

      <span className="relative min-w-0 flex-1">
        <span className={`block text-[14px] font-bold leading-tight truncate ${filled ? 'text-white' : t.title}`}>
          {title}
        </span>
        <span className={`block text-[11.5px] font-medium leading-tight mt-0.5 truncate
                          ${filled ? 'text-blue-50/80' : t.note}`}>
          {note}
        </span>
      </span>

      <span className={`relative grid place-items-center w-8 h-8 rounded-full flex-shrink-0 transition
                        group-hover:translate-x-0.5
                        ${filled ? 'bg-white/20 text-white' : t.arrow}`}>
        <ChevronRight size={16} />
      </span>
    </button>
  );
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wallet, balancePaise, balanceRupees, isActive, refreshWallet } = useWallet();

  // ── GA: dashboard load (once) ────────────────────────────────────────────
  useEffect(() => {
    gaUserDashboardLoad(user?.id, user?.role);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeTab, setActiveTab] = useState('Air-Export');
  const [searchAWB, setSearchAWB] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');   // the header search box
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCustomersAir, setSelectedCustomersAir] = useState([]);
  const [selectedCustomersSea, setSelectedCustomersSea] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [seaExportShipments, setSeaExportShipments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceMap, setInvoiceMap] = useState({});
  const [selectedForPayment, setSelectedForPayment] = useState(new Map());
  const [walletModal, setWalletModal] = useState(null); // { payload, totalAmount }
  const [trackingAwb, setTrackingAwb] = useState(null); // AWB number currently shown in the live tracking modal
  const [fobCalendarType, setFobCalendarType] = useState(null); // 'air' | 'sea' while the FOB calendar is open
  const closeFobCalendar = useCallback(() => setFobCalendarType(null), []);
  const [liveTrackingPrefixes, setLiveTrackingPrefixes] = useState(FALLBACK_LIVE_TRACKING_PREFIXES);

  // ── Pull the live list of carriers track.quickglobal.in currently supports,
  // instead of hardcoding it — falls back to the static list above on failure. ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(TRACKING_HEALTH_URL);
        if (!res.ok) return;
        const json = await res.json();
        const prefixes = (json?.enabledCarriers || []).map(c => c.prefix).filter(Boolean);
        if (!cancelled && prefixes.length > 0) setLiveTrackingPrefixes(new Set(prefixes));
      } catch (err) {
        console.warn('Could not refresh live-tracking carrier list, using fallback:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [loading, setLoading] = useState(false);
  const [loadingSeaExport, setLoadingSeaExport] = useState(false);
  const didInitialFetchRef = useRef(false);
  const lastFetchKeyRef = useRef('');
  const fetchShipmentsRef = useRef(null);
  const PAGE_SIZE = 20;
  const [airHasMore, setAirHasMore] = useState(true);
  const [airLoadingMore, setAirLoadingMore] = useState(false);
  const [seaHasMore, setSeaHasMore] = useState(true);
  const [seaLoadingMore, setSeaLoadingMore] = useState(false);
  const airSentinelRef = useRef(null);
  const seaSentinelRef = useRef(null);

  const selectedCustomers = activeTab === 'Sea-Export' ? selectedCustomersSea : selectedCustomersAir;

  // The list pages in as you scroll, so a filter could only ever match the
  // shipments already loaded — searching an AWB from further down the list
  // found nothing. The first time a filter goes on, the rest of the list is
  // fetched in one go; after that everything is here and it never runs again.
  const bookLoadedRef = useRef(false);
  const anyShipFilter = !!(
    searchAWB.trim() || selectedCountry || dateRange
    || selectedCustomersAir.length || selectedCustomersSea.length
  );

  useEffect(() => {
    if (!anyShipFilter || bookLoadedRef.current || !user?.id) return;
    bookLoadedRef.current = true;
    fetchShipmentsRef.current?.(true, 'both', 0, false, true);
  }, [anyShipFilter, user?.id]);
  const setSelectedCustomersForActiveTab = (next) => {
    if (activeTab === 'Sea-Export') {
      setSelectedCustomersSea(next);
    } else {
      setSelectedCustomersAir(next);
    }
  };

  const getAllTimestamps = (value) => {
    if (!value) return 0;

    const normalizeValues = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        const trimmed = val.trim();
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed;
        } catch {
          // ignore
        }
        if (trimmed.includes(' & ')) return trimmed.split(' & ');
        if (trimmed.includes(',')) return trimmed.split(',');
        return [trimmed];
      }
      return [val];
    };

    const toTimestamp = (val) => {
      if (!val) return 0;
      const str = String(val).trim();

      const ddmmyyyy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (ddmmyyyy) {
        const [, dd, mm, yyyy] = ddmmyyyy;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmyyDash = str.match(/^(\d{2})-(\d{2})-(\d{2,4})$/);
      if (ddmmyyDash) {
        let [, dd, mm, yy] = ddmmyyDash;
        let yyyy = parseInt(yy, 10);
        if (yyyy < 100) yyyy += 2000;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmmyy = str.match(/^(\d{2})-(\w{3})-(\d{2,4})$/);
      if (ddmmmyy) {
        const monthMap = {
          JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
          JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12'
        };
        const dd = ddmmmyy[1];
        const mmm = ddmmmyy[2].toUpperCase();
        let yyyy = parseInt(ddmmmyy[3], 10);
        if (yyyy < 100) yyyy += 2000;
        const mm = monthMap[mmm];
        if (mm) return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const parsed = new Date(str).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const values = normalizeValues(value);
    return values.map(toTimestamp).filter((ts) => ts > 0);
  };

  const getLatestTimestamp = (value) => {
    const timestamps = getAllTimestamps(value);
    return timestamps.length ? Math.max(...timestamps) : 0;
  };

  const getShipmentTimestamp = (shipment) => {
    const primary = getLatestTimestamp(shipment.sbDate);
    if (primary) return primary;

    const candidates = [
      shipment.bkg_date,
      shipment.bkgDate,
      shipment.createdAt,
      shipment.created_at,
      shipment.leoDate,
      shipment.invoiceNoDt
    ];
    return Math.max(...candidates.map(getLatestTimestamp));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
      if (showCountryDropdown && !event.target.closest('.country-dropdown-container')) {
        setShowCountryDropdown(false);
      }
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown, showCountryDropdown, showDatePicker]);

  // Fetch shipments from API
  useEffect(() => {
    // `all` asks for the whole list in one go rather than a page of it — a
    // filter has to match against every shipment, not just the ones scrolled
    // to so far.
    const fetchShipments = async (force = false, type = 'both', offset = 0, append = false, all = false) => {
      if (!user || !user.id) {
        setLoading(false);
        return;
      }

      const fetchKey = `${activeTab}-${user.id}`;
      if (!force && !append && didInitialFetchRef.current && lastFetchKeyRef.current === fetchKey) {
        return;
      }
      lastFetchKeyRef.current = fetchKey;
      if (!force) {
        didInitialFetchRef.current = true;
      }

      // Fetch Air Export shipments
      if (type !== 'sea') {
      if (activeTab === 'Air-Export' && !append) setLoading(true);
      try {
        const response = await apiCall(all
          ? `${API_ENDPOINTS.GET_USER_SHIPMENTS}?user_id=${user.id}`
          : `${API_ENDPOINTS.GET_USER_SHIPMENTS}?user_id=${user.id}&limit=${PAGE_SIZE}&offset=${offset}`, {
          method: 'GET'
        });

        const data = await response.json();
        console.log('User Shipments API Response:', data);

        if (response.ok && data.success) {
          const shipmentsArray = data.data || [];
          
          if (Array.isArray(shipmentsArray)) {
            const formattedShipments = shipmentsArray.map(shipment => {
              // Helper function to format numbers with commas
              const formatNumber = (num) => {
                if (!num || num === '-' || num === '0') return num;

                if (Array.isArray(num)) {
                  return num.map(val => formatNumber(val)).join(' & ');
                }

                if (num.toString().includes(' & ')) {
                  return num
                    .toString()
                    .split(' & ')
                    .map(val => formatNumber(val.trim()))
                    .join(' & ');
                }

                const numStr = num.toString().replace(/,/g, '');
                const parts = numStr.split('.');
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                return parts.join('.');
              };

              // Helper function to parse JSON string to array if needed
              const parseField = (field) => {
                if (!field) return [];
                if (Array.isArray(field)) return field;
                if (typeof field === 'string') {
                  try {
                    const parsed = JSON.parse(field);
                    return Array.isArray(parsed) ? parsed : [field];
                  } catch {
                    return [field];
                  }
                }
                return [field];
              };

              // Helper function to join arrays with & (with unique values only)
              const joinWithAnd = (value) => {
                const arr = parseField(value);
                const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
                // Remove duplicates using Set
                const unique = [...new Set(filtered)];
                return unique.join(' & ') || '-';
              };

              // Helper function to format dates - convert to DD/MM/YYYY
              const formatDates = (value) => {
                const arr = parseField(value);
                const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
                
                if (filtered.length === 0) return '-';
                
                // Month mapping
                const monthMap = {
                  'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
                  'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
                };
                
                // Convert dates to DD/MM/YYYY format first
                const formattedDates = filtered.map(date => {
                  if (!date) return null;
                  let dateStr = date;
                  // Remove time part if present
                  if (typeof date === 'string' && date.includes(' ')) {
                    dateStr = date.split(' ')[0];
                  }
                  
                  // Check if already in DD/MM/YYYY format
                  const ddmmyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
                  const match = dateStr.match(ddmmyyyyPattern);
                  if (match) {
                    return dateStr; // Already in correct format
                  }
                  
                  // Check for DD-MMM-YYY or DD-MMM-YYYY format (e.g., "15-NOV-025")
                  const ddmmmyyyPattern = /^(\d{2})-(\w{3})-(\d{2,4})$/;
                  const mmmMatch = dateStr.match(ddmmmyyyPattern);
                  if (mmmMatch) {
                    const day = parseInt(mmmMatch[1]);
                    const monthStr = mmmMatch[2].toUpperCase();
                    let year = parseInt(mmmMatch[3]);
                    
                    // Fix year: 025 -> 2025, 25 -> 2025
                    if (year < 100) {
                      year += 2000;
                    }
                    
                    const month = monthMap[monthStr];
                    if (month !== undefined) {
                      return `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;
                    }
                  }
                  
                  // Parse and format to DD/MM/YYYY
                  try {
                    const dateObj = new Date(dateStr);
                    if (!isNaN(dateObj.getTime())) {
                      let day = dateObj.getDate();
                      let month = dateObj.getMonth() + 1;
                      let year = dateObj.getFullYear();
                      
                      // Fix for years like 025 (should be 2025)
                      if (year < 100) {
                        year += 2000;
                      }
                      
                      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
                    }
                  } catch (e) {
                    console.error('Date parsing error:', e);
                  }
                  return null;
                }).filter(d => d !== null);
                
                // Remove duplicates AFTER formatting
                const unique = [...new Set(formattedDates)];
                return unique.join(' & ') || '-';
              };

              // Format FOB values with rupee sign (with unique values only)
              const formatFobValue = (value) => {
                const arr = parseField(value);
                const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
                // Remove duplicates using Set
                const unique = [...new Set(filtered)];
                return unique.map(val => {
                  const cleanVal = val.replace(/[₹,]/g, '');
                  return `₹${formatNumber(cleanVal)}`;
                }).join(' & ') || '-';
              };

              return {
                id: shipment.id,
                awbNo: shipment.awbNumber || '',
                awb_no: shipment.awbNumber || '',
                awbNumber: shipment.awbNumber || '',
                sbNo: joinWithAnd(shipment.sbNumbers),
                sbDate: formatDates(shipment.sbDates),
                origin: 'DEL',
                originCity: 'Delhi',
                destination: shipment.destCode || '',
                destCity: shipment.destCity || '',
                destCountry: shipment.destCountry || '',
                flightNo: joinWithAnd(shipment.flightNumbers),
                flightDate: formatDates(shipment.flightDates),
                totalPkt: shipment.numberOfPackages || '-',
                grWeight: formatNumber(shipment.grossWeight) || '-',
                chWeight: formatNumber(shipment.chargeableWeight) || '-',
                fobValue: formatFobValue(shipment.sbFobValues),
                dbk: formatNumber(shipment.DBK) || '0',
                rodtep: formatNumber(shipment.Rodtep) || '0',
                customer: shipment.customerName || shipment.username || '-',
                igst: formatNumber(shipment.IGST) || '0',
                rosctl: formatNumber(shipment.Rosctl) || '0',
                fcValue: formatNumber(shipment.fcValue) || '-',
                fcType: shipment.fcType || '-',
                fcRate: formatNumber(shipment.fcRate) || '-',
                buyerName: shipment.buyerName || '-',
                shipmentType: shipment.shipmentType || activeTab,
                awbPdf: shipment.awbPdf || '',
                leoPdf: shipment.leoPdfKey || shipment.leoPdf || '',
                invPdf: shipment.invPdf || '',
                invoicePdf: shipment.invoicePdf || shipment.invoice || '',
                created_at: shipment.createdAt || shipment.created_at || shipment.bkgDate || '',
                bkg_date: shipment.bkgDate || shipment.created_at || shipment.createdAt || '',
                createdAt: shipment.createdAt || shipment.created_at || shipment.bkgDate || '',
                paymentDetails: shipment.payment_details || null
              };
            });
            
            // Remove duplicates by AWB number
            const uniqueShipments = formattedShipments.filter((shipment, index, self) =>
              index === self.findIndex((s) => s.awbNo === shipment.awbNo)
            );
            
            console.log('Total shipments:', formattedShipments.length, 'Unique:', uniqueShipments.length);
            const sortedShipments = [...uniqueShipments].sort(
              (a, b) => getShipmentTimestamp(b) - getShipmentTimestamp(a)
            );
            console.log('Sorted Air Export shipments (latest first by SB date):', sortedShipments.map(s => ({
              awbNo: s.awbNo,
              sbDate: s.sbDate,
              createdAt: s.createdAt,
              created_at: s.created_at,
              bkg_date: s.bkg_date
            })));
            if (append) {
              setShipments(prev => {
                const existing = new Set(prev.map(s => s.awbNo));
                return [...prev, ...sortedShipments.filter(s => !existing.has(s.awbNo))];
              });
            } else {
              setShipments(sortedShipments);
              gaShipmentsLoaded(sortedShipments.length, 'Air-Export');
              if (user && user.id) {
                localStorage.setItem(`userShipments_${user.id}`, JSON.stringify(sortedShipments));
              }
            }
            setAirHasMore(all ? false : shipmentsArray.length === PAGE_SIZE);
          }
        }
      } catch (error) {
        console.error('Error fetching air shipments:', error);
      } finally {
        if (activeTab === 'Air-Export') setLoading(false);
      }
      } // end if (type !== 'sea')

      // Fetch Sea Export shipments
      if (type !== 'air') {
        if (activeTab === 'Sea-Export' && !append) setLoadingSeaExport(true);
        try {
          const seaOffset = type === 'both' ? 0 : offset;
          const response = await apiCall(all
            ? `/sea-export/user/${user.id}`
            : `/sea-export/user/${user.id}?limit=${PAGE_SIZE}&offset=${seaOffset}`, {
            method: 'GET'
          });
          
          const data = await response.json();
          console.log('Sea Export API Response:', data);
          console.log('Current user ID:', user.id);
          
          if (response.ok && data.success) {
            // Filter shipments for current user only - convert both to string for comparison
            const userSeaShipments = (data.data || []).filter(s => {
              console.log('Comparing userId:', s.userId, 'with user.id:', user.id);
              return String(s.userId) === String(user.id);
            });
            
            console.log('Filtered sea shipments for user:', userSeaShipments.length);
            
            const formattedShipments = userSeaShipments.map(s => ({
              id: s.id,
              portCode: s.port_code || s.portCode || '-',
              sbNo: s.sb_no || s.sbNo || '-',
              sbDate: s.sb_date || s.sbDate || '-',
              pkg: s.pkg || '-',
              gwt: s.gwt || '-',
              portOfLoading: s.port_of_loading || s.portOfLoading || '-',
              stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
              portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
              portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
              portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
              portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
              countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
              exporterName: s.exporter_name || s.exporterName || '-',
              buyerName: s.buyer_name || s.buyerName || '-',
              consigneeName: s.consignee_name || s.consigneeName || '-',
              mode: s.mode || '-',
              lut: s.lut || '-',
              fobValue: s.fob_value || s.fobValue || '-',
              fcValue: s.fc_value || s.fcValue || s.inv_amt || s.invAmt || s.invoiceAmount || '-',
              dbkClaim: s.dbk_claim || s.dbkClaim || '-',
              igstAmt: s.igst_amt || s.igstAmt || '-',
              cessAmt: s.cess_amt || s.cessAmt || '-',
              igstValue: s.igst_value || s.igstValue || '-',
              rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
              rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
              invNo: s.inv_no || s.invNo || '-',
              invAmt: s.inv_amt || s.invAmt || '-',
              currency: s.currency || '-',
              leoDate: s.leo_date || s.leoDate || '-',
              invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
              blNumber: s.bl_number || s.blNumber || '-',
              containerNo: normalizeSeaContainerNoDisplay(s.container_no || s.containerNo),
              containerQty: s.container_qty || s.containerQty || '-',
              leoPdf: s.leoPdfKey || s.leo_pdf || s.leoPdf || '',
              blPdf: s.bl_pdf || s.blPdf || '',
              invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
              invoicePdf: s.invoice_pdf || s.invoicePdf || '',
              purPdf: s.pur_pdf || s.purPdf || '',
              editedBy: s.edited_by || s.editedBy || '-',
              createdAt: s.created_at || s.createdAt || new Date().toISOString(),
              paymentDetails: s.payment_details || null,
              type: 'Sea-Export'
            }));
            
            const sortedSeaExportShipments = [...formattedShipments].sort(
              (a, b) => getShipmentTimestamp(b) - getShipmentTimestamp(a)
            );
            console.log('Sorted Sea Export shipments (latest first by SB date):', sortedSeaExportShipments.map(s => ({
              blNumber: s.blNumber,
              sbDate: s.sbDate,
              createdAt: s.createdAt,
              leoDate: s.leoDate
            })));
            if (append) {
              setSeaExportShipments(prev => {
                const existing = new Set(prev.map(s => s.blNumber));
                return [...prev, ...sortedSeaExportShipments.filter(s => !existing.has(s.blNumber))];
              });
            } else {
              setSeaExportShipments(sortedSeaExportShipments);
              if (user && user.id) {
                localStorage.setItem(`userSeaShipments_${user.id}`, JSON.stringify(sortedSeaExportShipments));
              }
            }
            setSeaHasMore(all ? false : userSeaShipments.length === PAGE_SIZE);
          }
        } catch (error) {
          console.error('Error fetching sea export shipments:', error);
        } finally {
          if (activeTab === 'Sea-Export') setLoadingSeaExport(false);
        }
      } // end if (type !== 'air')
    };

    fetchShipmentsRef.current = fetchShipments;
    fetchShipments();

    // Listen for shipment creation events for instant notification
    const handleShipmentCreated = () => {
      console.log('New shipment created - refreshing...');
      fetchShipments(true);
    };
    
    window.addEventListener('shipmentCreated', handleShipmentCreated);
    
    // Listen for localStorage changes (cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === 'newShipmentNotification') {
        console.log('New shipment notification received - refreshing...');
        fetchShipments(true);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('shipmentCreated', handleShipmentCreated);
      window.removeEventListener('storage', handleStorageChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.id]);

  // ── Load More (infinite scroll) ──
  const loadMoreAir = useCallback(async () => {
    if (airLoadingMore || !airHasMore || !fetchShipmentsRef.current) return;
    setAirLoadingMore(true);
    await fetchShipmentsRef.current(true, 'air', shipments.length, true);
    setAirLoadingMore(false);
  }, [airLoadingMore, airHasMore, shipments.length]);

  const loadMoreSea = useCallback(async () => {
    if (seaLoadingMore || !seaHasMore || !fetchShipmentsRef.current) return;
    setSeaLoadingMore(true);
    await fetchShipmentsRef.current(true, 'sea', seaExportShipments.length, true);
    setSeaLoadingMore(false);
  }, [seaLoadingMore, seaHasMore, seaExportShipments.length]);

  // IntersectionObserver — trigger load more when sentinel enters viewport
  useEffect(() => {
    if (!airSentinelRef.current) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) loadMoreAir(); }, { threshold: 0.1 });
    obs.observe(airSentinelRef.current);
    return () => obs.disconnect();
  }, [loadMoreAir]);

  useEffect(() => {
    if (!seaSentinelRef.current) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) loadMoreSea(); }, { threshold: 0.1 });
    obs.observe(seaSentinelRef.current);
    return () => obs.disconnect();
  }, [loadMoreSea]);

  // Fetch invoices for this user
  useEffect(() => {
    if (!user?.id) return;
    const fetchInvoices = async () => {
      try {
        const res = await apiCall(`${API_ENDPOINTS.INVOICE_GET_BY_USER}/${user.id}`, { method: 'GET' });
        const data = await res.json();
        if (res.ok && data.success) {
          setInvoices(data.data || []);
        } else if (Array.isArray(data)) {
          setInvoices(data);
        }
      } catch (err) {
        console.error('Invoices fetch error:', err);
      }
    };
    fetchInvoices();
  }, [user?.id]);

  // Normalize AWB/BL key — strip dashes, spaces for consistent matching
  const normalizeKey = (k) => String(k || '').replace(/[-\s]/g, '').toUpperCase();

  // Build invoice map keyed by normalized awb_number / bl_number
  useEffect(() => {
    if (!invoices.length) return;
    const map = {};
    invoices.forEach(inv => {
      const raw = inv.awb_number || inv.awbNumber || inv.bl_number || inv.blNumber;
      if (!raw) return;
      const key = normalizeKey(raw);
      const matched = shipments.find(s => normalizeKey(s.awbNo) === key || normalizeKey(s.blNumber) === key);
      const shipmentType = matched?.shipmentType || 'Air-Export';
      map[key] = { ...inv, shipmentType };
    });
    setInvoiceMap(map);
  }, [invoices, shipments]);

  // ── Payment helpers ──────────────────────────────────────────────────────────
  const getShipmentType = (type) => {
    if (type === 'gmr') return 'gmr_shipment';
    if (type === 'dcsc') return 'dcsc_shipment';
    if (type === 'Sea-Export') return 'sea_export_shipment';
    return 'air_export_shipment';
  };

  const togglePaymentSelect = (key, shipment, invData) => {
    setSelectedForPayment(prev => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          id: shipment.id,
          shipmentType: invData?.shipmentType || shipment.shipmentType || 'Air-Export',
          grand_total: invData?.summary?.grand_total || 0,
          label: key,
        });
      }
      return next;
    });
  };

  // ── Core Razorpay opener — onAfterVerify optional callback (for split pay wallet debit) ──
  // ── Helper: open Razorpay modal given order_id + amount ──
  const _openRazorpayModal = (orderData, amountPaise, onSuccess) => {
    const options = {
      key: RAZORPAY_KEY_ID,
      amount: amountPaise,
      currency: 'INR',
      order_id: orderData.order_id,
      name: 'QuickLook',
      description: 'Shipment Payment',
      prefill: {
        name: user?.name || user?.username || '',
        email: user?.email || '',
        contact: user?.phone || user?.mobile || '9999999999',
      },
      handler: async (response) => {
        try {
          const vRes = await apiCall('/payments/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          const vData = await vRes.json();
          if (vRes.ok && vData.success) {
            if (onSuccess) onSuccess();
            setSelectedForPayment(new Map());
            setWalletModal(null);
            fetchShipmentsRef.current?.(true);
          } else {
            toast.error('Payment verification failed. Contact support.');
          }
        } catch { toast.error('Payment verification error.'); }
      },
      modal: { ondismiss: () => {} },
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (e) => toast.error(`Payment failed: ${e.error.description}`));
    rzp.open();
  };

  // ── Razorpay-only payment ──
  const openRazorpay = async (payload) => {
    try {
      const shipments = payload.shipments;
      const totalAmountPaise = shipments.reduce((sum, s) => sum + s.amount_paise, 0);
      const res = await apiCall('/payments/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method: 'razorpay', shipments }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.message || 'Failed to create order'); return; }
      const orderData = data.data || data;
      _openRazorpayModal(orderData, totalAmountPaise, () => toast.success('Payment successful!'));
    } catch (err) { console.error('openRazorpay error:', err); toast.error('Payment error. Please try again.'); }
  };

  // ── Full wallet payment ──
  const payViaWallet = async (payload, walletAmountRupees) => {
    try {
      const res = await apiCall('/payments/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method: 'wallet', shipments: payload.shipments }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`₹${walletAmountRupees.toLocaleString('en-IN')} paid from wallet!`);
        refreshWallet();
        setSelectedForPayment(new Map());
        setWalletModal(null);
        fetchShipmentsRef.current?.(true);
      } else {
        toast.error(data.message || 'Wallet payment failed');
      }
    } catch { toast.error('Wallet payment error. Please try again.'); }
  };

  // ── Mixed payment: Razorpay first, wallet debit only after Razorpay success ──
  const openSplitPay = async (payload, totalAmount, walletAmountRupees) => {
    setWalletModal(null);
    const walletAmountPaise = Math.round(walletAmountRupees * 100);
    const razorpayAmountRupees = totalAmount - walletAmountRupees;
    const razorpayAmountPaise = Math.round(razorpayAmountRupees * 100);
    const ratio = razorpayAmountRupees / totalAmount;
    // Adjust shipment amounts to reflect only the razorpay portion
    const razorpayShipments = payload.shipments.map(s => ({
      ...s,
      amount_paise: Math.max(100, Math.round(s.amount_paise * ratio)),
    }));
    try {
      const res = await apiCall('/payments/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method: 'razorpay', shipments: razorpayShipments }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.message || 'Failed to initiate payment'); return; }
      const orderData = data.data || data;
      // After Razorpay success → THEN debit wallet
      const afterVerify = async () => {
        const wRes = await apiCall(`${API_ENDPOINTS.WALLET_ALL}/${wallet.id}/debit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount_paise: walletAmountPaise, source: 'shipment_payment', remarks: 'Wallet portion of split payment' }),
        });
        const wData = await wRes.json();
        if (wRes.ok && wData.success) {
          toast.success(`Paid! ₹${walletAmountRupees.toLocaleString('en-IN')} from wallet + ₹${razorpayAmountRupees.toLocaleString('en-IN')} via card/UPI`);
          refreshWallet();
        } else {
          toast.error(`Card payment done but wallet debit failed: ${wData.message || 'Contact support'}`);
        }
      };
      _openRazorpayModal(orderData, razorpayAmountPaise, afterVerify);
    } catch (err) { console.error('openSplitPay error:', err); toast.error('Payment error. Please try again.'); }
  };

  const handlePayNow = async (shipment, invData) => {
    const grandTotal =
      invData?.summary?.grand_total ||
      invData?.invoice?.summary?.grand_total ||
      invData?.grandTotal ||
      invData?.grand_total ||
      0;
    if (!grandTotal) { toast.error('Invoice amount not found'); return; }
    if (grandTotal > 500000) { toast.error('Payment above ₹5,00,000 is not allowed in a single transaction.'); return; }
    const payload = {
      currency: 'INR',
      receipt: `rcpt_${shipment.awbNo || shipment.blNumber}_${Date.now()}`,
      shipments: [{
        shipment_type: getShipmentType(invData?.shipmentType || shipment.shipmentType),
        shipment_ref_id: shipment.id,
        amount_paise: grandTotal * 100,
      }],
    };
    // Always show wallet modal so user can choose payment method
    setWalletModal({ payload, totalAmount: grandTotal });
  };

  const handleBulkPay = async () => {
    const selected = Array.from(selectedForPayment.values());
    if (!selected.length) return;
    const total = selected.reduce((sum, s) => sum + s.grand_total, 0);
    if (total > 500000) { toast.error('Bulk total exceeds ₹5,00,000. Please pay each shipment individually.'); return; }
    const payload = {
      currency: 'INR',
      receipt: `rcpt_bulk_${Date.now()}`,
      shipments: selected.map(s => ({
        shipment_type: getShipmentType(s.shipmentType),
        shipment_ref_id: s.id,
        amount_paise: s.grand_total * 100,
      })),
    };
    if (wallet && isActive && balancePaise > 0) {
      setWalletModal({ payload, totalAmount: total });
    } else {
      await openRazorpay(payload);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const normalizePdfList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch {
        if (value.includes(',')) {
          return value.split(',').map(v => v.trim()).filter(Boolean);
        }
        return [value];
      }
      return [value];
    }
    return [value];
  };

  // Helper to get single PDF path from array or string
  const getSinglePdfPath = (value) => {
    if (!value) return '';
    if (Array.isArray(value)) return value[0] || '';
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed[0] || '';
        return value;
      } catch {
        return value;
      }
    }
    return value;
  };

  // Helper to check if PDF field has valid data (handles both array and string)
  const hasPdfData = (value) => {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0 && value.some(v => v && v.trim && v.trim() !== '');
    if (typeof value === 'string') {
      if (value === '[]' || value.trim() === '') return false;
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.length > 0 && parsed.some(v => v && v.trim && v.trim() !== '');
        return true;
      } catch {
        return value.trim() !== '';
      }
    }
    return false;
  };

  const openMultipleTabs = (urls, label = 'documents') => {
    if (!urls || urls.length === 0) return;
    let blockedCount = 0;

    urls.forEach((url) => {
      const win = window.open(url, '_blank');
      if (!win) {
        blockedCount += 1;
      } else {
        try {
          win.opener = null;
        } catch (e) {
          // no-op
        }
      }
    });

    if (blockedCount > 0 && urls.length > 1) {
      toast.error(`Popup blocked. Please open the remaining ${label} from the list.`);

      const helper = window.open('', '_blank');
      if (helper) {
        const listItems = urls.map((url, index) => `<li style="margin: 8px 0;"><a href="${url}" target="_blank" rel="noopener noreferrer">${label} ${index + 1}</a></li>`).join('');
        helper.document.write(`
          <html>
            <head>
              <title>Open ${label}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 16px; }
                h2 { margin: 0 0 12px; }
              </style>
            </head>
            <body>
              <h2>Open ${label}</h2>
              <p>Click each link to open in a new tab.</p>
              <ul>${listItems}</ul>
            </body>
          </html>
        `);
      }
    }
  };

  const handleDownloadPDF = async (shipment, docType) => {
    // ── GA: per-type pdf events ────────────────────────────────────────────
    const awb = shipment.awbNumber || shipment.awb || '';
    if (docType === 'AWB Copy')  gaDownloadAWBCopy(awb, activeTab);
    else if (docType === 'INV/PL')  gaDownloadINV(awb, activeTab);
    else if (docType === 'INVOICE') gaDownloadInvoice(awb, activeTab);
    // LEO count tracked below after list is built
    try {
      let pdfPath = '';
      
      // Determine PDF path based on document type
      switch(docType) {
        case 'AWB Copy':
          pdfPath = shipment.awbPdf;
          break;
        case 'LEO':
          pdfPath = shipment.leoPdf;
          break;
        case 'INV/PL':
          pdfPath = shipment.invPdf || shipment.invPlPdf;
          break;
        case 'INVOICE':
          pdfPath = shipment.invoicePdf;
          break;
        default:
          console.error('Unknown document type:', docType);
          return;
      }

      if (!pdfPath) {
        toast.error(`${docType} not available for this shipment.`);
        return;
      }

      // Use normalizePdfList for all PDF types to handle arrays
      const pdfList = normalizePdfList(pdfPath);
      if (pdfList.length === 0) {
        toast.error(`${docType} not available for this shipment.`);
        return;
      }

      if (docType === 'LEO') {
        gaDownloadLEO(awb, pdfList.length, activeTab);
      }

      // Invoice PDFs get re-uploaded to the same S3 key on edit, so bust the CDN/browser
      // cache with a version param derived from the shipment's last update time.
      const version = docType === 'INVOICE' && shipment.updatedAt ? new Date(shipment.updatedAt).getTime() : null;
      const urls = pdfList.map((key) => {
        const base = key.startsWith('http') ? key : getCloudFrontUrl(key);
        return version ? `${base}${base.includes('?') ? '&' : '?'}v=${version}` : base;
      }).filter(Boolean);
      openMultipleTabs(urls, `${docType} PDFs`);

    } catch (error) {
      console.error('Error opening PDF:', error);
      toast.error(`Error opening ${docType}. Please try again.`);
    }
  };

  const handleNewBooking = () => {
    navigate('/user/new-booking');
  };

  const handleExportCSV = () => {
    const isSeaExport = activeTab === 'Sea-Export';
    const exportShipments = isSeaExport ? seaExportShipments : filteredShipments;

    if (exportShipments.length === 0) {
      toast.error('No shipments to export');
      return;
    }

    // Helper function to clean currency values (remove rupee sign and commas)
    const cleanCurrency = (value) => {
      if (!value || value === '-' || value === '') return '';
      return value.toString().replace(/₹/g, '').replace(/,/g, '').trim();
    };

    const formatAwbForCsv = (awb) => {
      if (awb === null || awb === undefined || awb === '') return '';
      const str = String(awb);
      return str.startsWith('0') ? `="${str}"` : str;
    };


    const headers = isSeaExport
      ? [
          'BL No.',
          'S/B No.',
          'S/B Date',
          'Port Code',
          'Port of Loading',
          'Port of Discharge',
          'Final Destination',
          'Country',
          'Exporter',
          'Buyer',
          'Consignee',
          'Mode',
          'LUT',
          'FOB',
          'DBK',
          'IGST',
          'Cess',
          'IGST Value',
          'Rodtep',
          'Rosctl',
          'INV No.',
          'INV Amt',
          'Currency',
          'LEO Date',
          'Invoice No Dt',
          'Container No',
          'Container Qty',
          'BL PDF',
          'LEO PDF',
          'INV/PL PDF',
          'INVOICE',
          'PUR'
        ]
      : [
          'AWB No.',
          'Customer Name',
          'S/B No.',
          'S/B Date',
          'City Code',
          'Dest City',
          'Dest Country',
          'Flight No.',
          'Flight Date',
          'Total Pkt',
          'Weight',
          'CH Weight',
          'FOB',
          'DBK',
          'Rodtep',
          'Rosctl',
          'IGST',
          'FC Value',
          'FC Type',
          'FC Rate',
          'Buyer Name',
          'AWB',
          'LEO',
          'INV/PL',
          'INVOICE'
        ];

    const rows = isSeaExport
      ? exportShipments.map(shipment => [
          shipment.blNumber || '',
          shipment.sbNo || '',
          shipment.sbDate || '',
          shipment.portCode || '',
          shipment.portOfLoading || '',
          shipment.portOfDischarge || '',
          shipment.portOfFinalDestination || '',
          shipment.countryOfFinalDestination || '',
          shipment.exporterName || '',
          shipment.buyerName || '',
          shipment.consigneeName || '',
          shipment.mode || '',
          shipment.lut || '',
          cleanCurrency(shipment.fobValue || ''),
          cleanCurrency(shipment.dbkClaim || ''),
          cleanCurrency(shipment.igstAmt || ''),
          cleanCurrency(shipment.cessAmt || ''),
          cleanCurrency(shipment.igstValue || ''),
          cleanCurrency(shipment.rodtepAmt || ''),
          cleanCurrency(shipment.rosctlAmt || ''),
          shipment.invNo || '',
          cleanCurrency(shipment.invAmt || ''),
          shipment.currency || '',
          shipment.leoDate || '',
          shipment.invoiceNoDt || '',
          shipment.containerNo || '',
          shipment.containerQty || '',
          shipment.blPdf ? 'Yes' : 'No',
          shipment.leoPdf ? 'Yes' : 'No',
          shipment.invPlPdf ? 'Yes' : 'No',
          shipment.invoicePdf ? 'Yes' : 'No',
          shipment.purPdf ? 'Yes' : 'No'
        ])
      : exportShipments.map(shipment => [
          formatAwbForCsv(shipment.awbNo || shipment.awbNumber || ''),
          shipment.customer || shipment.customerName || '',
          shipment.sbNo || shipment.sbNumber || '',
          shipment.sbDate || '',
          shipment.origin || shipment.cityCode || '',
          shipment.destCity || '',
          shipment.destCountry || '',
          shipment.flightNo || '',
          shipment.flightDate || '',
          shipment.totalPkt || '',
          cleanCurrency(shipment.grWeight || shipment.weight || ''),
          cleanCurrency(shipment.chWeight || ''),
          cleanCurrency(shipment.fobValue || shipment.fob || ''),
          cleanCurrency(shipment.dbk || ''),
          cleanCurrency(shipment.rodtep || ''),
          cleanCurrency(shipment.rosctl || ''),
          cleanCurrency(shipment.igst || ''),
          shipment.fcValue || '',
          shipment.fcType || '',
          shipment.fcRate || '',
          shipment.buyerName || '',
          shipment.awbPdf ? 'Yes' : 'No',
          shipment.leoPdf ? 'Yes' : 'No',
          shipment.invPdf ? 'Yes' : 'No',
          shipment.invoicePdf ? 'Yes' : 'No'
        ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(escapeCsv).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `shipments_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`Exported ${exportShipments.length} shipments to CSV`);
  };

  const resetFilters = () => {
    setSearchAWB('');
    setGlobalSearch('');
    setDateRange(null);
    setSelectedCountry('');
    setSelectedCustomersForActiveTab([]);
  };

  const formatDateRange = () => {
    if (!dateRange || !dateRange[0]) {
      return 'Select date range';
    }
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const getFilterSourceShipments = () => (
    activeTab === 'Sea-Export' ? seaExportShipments : shipments
  );

  const getShipmentCustomerCandidates = (shipment) => {
    if (activeTab === 'Sea-Export') {
      return [shipment.consigneeName || shipment.exporterName || shipment.buyerName]
        .map(v => String(v || '').trim())
        .filter(v => v && v !== '-');
    }
    return [shipment.customer]
      .map(v => String(v || '').trim())
      .filter(v => v && v !== '-');
  };

  const getShipmentCountryValue = (shipment) => {
    if (activeTab === 'Sea-Export') {
      return String(shipment.countryOfFinalDestination || '').trim();
    }
    return String(shipment.destCountry || '').trim();
  };

  // Get unique customers with shipment counts from API data
  const getCustomersWithCount = () => {
    const customerMap = {};
    getFilterSourceShipments().forEach(shipment => {
      const names = getShipmentCustomerCandidates(shipment);
      names.forEach((name) => {
        if (customerMap[name]) {
          customerMap[name]++;
        } else {
          customerMap[name] = 1;
        }
      });
    });
    return Object.entries(customerMap).map(([name, count]) => ({
      name,
      count
    }));
  };

  // Get unique countries from shipments
  const getUniqueCountries = () => {
    const countries = new Set();
    getFilterSourceShipments().forEach(shipment => {
      const country = getShipmentCountryValue(shipment);
      if (country && country !== '-') {
        countries.add(country);
      }
    });
    return Array.from(countries).sort();
  };

  const customers = getCustomersWithCount();
  const countries = getUniqueCountries();

  // Filter shipments based on selected filters
  // The header search is not an AWB box — it matches anything printed on the
  // card, so "DANBOL", "2827250", "LOS" and an AWB all narrow the same list.
  const matchesGlobalSearch = (shipment) => {
    const q = globalSearch.trim().toLowerCase();
    if (!q) return true;
    const flat = (v) => (Array.isArray(v) ? v.join(' ') : String(v ?? ''));
    return [
      shipment.awbNo, shipment.awbNumber, shipment.blNumber, shipment.sbNo, shipment.sbDate,
      shipment.customer, shipment.customerName, shipment.consigneeName, shipment.buyerName,
      shipment.origin, shipment.originCity, shipment.originCountry,
      shipment.destination, shipment.destCity, shipment.destCountry,
      shipment.portOfLoading, shipment.portOfDischarge, shipment.countryOfFinalDestination,
      shipment.flightNo, shipment.flightDate, shipment.containerNo, shipment.invNo,
      shipment.fobValue, shipment.fcValue,
    ].map(flat).join(' ').toLowerCase().includes(q);
  };

  const filteredShipments = shipments.filter(shipment => {
    if (!matchesGlobalSearch(shipment)) return false;

    // Filter by AWB number
    if (searchAWB && !shipment.awbNo.toLowerCase().includes(searchAWB.toLowerCase())) {
      return false;
    }

    // Filter by customer names
    if (selectedCustomers.length > 0 && !selectedCustomers.includes(shipment.customer)) {
      return false;
    }

    // Filter by country
    if (selectedCountry && shipment.destCountry !== selectedCountry) {
      return false;
    }

    // Filter by date range (only if user has selected a date range)
    if (dateRange && dateRange[0] && dateRange[0].startDate && dateRange[0].endDate) {
      const startDate = new Date(dateRange[0].startDate);
      const endDate = new Date(dateRange[0].endDate);

      // Reset time to compare only dates
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const startTs = startDate.getTime();
      const endTs = endDate.getTime();

      const sbTimestamps = getAllTimestamps(shipment.sbDate);
      const fallbackTimestamps = getAllTimestamps(
        shipment.bkg_date || shipment.bkgDate || shipment.createdAt || shipment.created_at
      );

      const timestampsToCheck = sbTimestamps.length ? sbTimestamps : fallbackTimestamps;

      if (!timestampsToCheck.length) {
        return false;
      }

      const inRange = timestampsToCheck.some((ts) => ts >= startTs && ts <= endTs);
      if (!inRange) return false;
    }

    return true;
  });

  // Only shipments that already have an invoice can go into a bulk payment.
  const payableAir = filteredShipments.filter((sh) => hasPdfData(sh.invoicePdf)
    && sh.paymentDetails?.shipment_payment_status !== 'paid');
  const selectedTotal = Array.from(selectedForPayment.values()).reduce((a, x) => a + (x.grand_total || 0), 0);
  const allAirSelected = payableAir.length > 0 && payableAir.every((sh) => selectedForPayment.has(sh.awbNo));
  const toggleSelectAllAir = () => {
    if (allAirSelected) { setSelectedForPayment(new Map()); return; }
    const next = new Map(selectedForPayment);
    payableAir.forEach((sh) => {
      const inv = invoiceMap[normalizeKey(sh.awbNo)];
      next.set(sh.awbNo, {
        id: sh.id,
        shipmentType: inv?.shipmentType || sh.shipmentType || 'Air-Export',
        grand_total: inv?.summary?.grand_total || 0,
        label: sh.awbNo,
      });
    });
    setSelectedForPayment(next);
  };

  const sortedFilteredShipments = [...filteredShipments].sort((a, b) => {
    const aTs = getLatestTimestamp(a.sbDate) || getShipmentTimestamp(a);
    const bTs = getLatestTimestamp(b.sbDate) || getShipmentTimestamp(b);
    return bTs - aTs;
  });

  const filteredSeaShipments = seaExportShipments.filter((shipment) => {
    if (!matchesGlobalSearch(shipment)) return false;

    if (searchAWB) {
      const seaSearchText = `${shipment.blNumber || ''} ${shipment.sbNo || ''}`.toLowerCase();
      if (!seaSearchText.includes(searchAWB.toLowerCase())) {
        return false;
      }
    }

    if (selectedCustomers.length > 0) {
      const candidates = getShipmentCustomerCandidates(shipment);
      const hasCustomerMatch = candidates.some(name => selectedCustomers.includes(name));
      if (!hasCustomerMatch) {
        return false;
      }
    }

    if (selectedCountry && shipment.countryOfFinalDestination !== selectedCountry) {
      return false;
    }

    if (dateRange && dateRange[0] && dateRange[0].startDate && dateRange[0].endDate) {
      const startDate = new Date(dateRange[0].startDate);
      const endDate = new Date(dateRange[0].endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const startTs = startDate.getTime();
      const endTs = endDate.getTime();

      const sbTimestamps = getAllTimestamps(shipment.sbDate);
      const fallbackTimestamps = getAllTimestamps(
        shipment.createdAt || shipment.leoDate || shipment.invoiceNoDt
      );
      const timestampsToCheck = sbTimestamps.length ? sbTimestamps : fallbackTimestamps;

      if (!timestampsToCheck.length) {
        return false;
      }

      const inRange = timestampsToCheck.some((ts) => ts >= startTs && ts <= endTs);
      if (!inRange) return false;
    }

    return true;
  });

  const sortedFilteredSeaShipments = [...filteredSeaShipments].sort((a, b) => {
    const aTs = getLatestTimestamp(a.sbDate) || getShipmentTimestamp(a);
    const bTs = getLatestTimestamp(b.sbDate) || getShipmentTimestamp(b);
    return bTs - aTs;
  });

  const handleCustomerToggle = (customerName) => {
    let next;
    if (selectedCustomers.includes(customerName)) {
      next = selectedCustomers.filter(c => c !== customerName);
    } else {
      next = [...selectedCustomers, customerName];
    }
    setSelectedCustomersForActiveTab(next);
    gaCustomerFilter(next.length, activeTab);
  };

  const removeCustomer = (customerName) => {
    setSelectedCustomersForActiveTab(selectedCustomers.filter(c => c !== customerName));
  };

  const renderCustomerFilterControl = () => (
    <div className="relative customer-dropdown-container group">
      <label className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">
        <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
        Customer Name
      </label>
      <div className="relative">
        <div
          onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
          className="w-full h-10 px-3.5 py-0 text-[13px] border border-slate-200 dark:border-slate-700/70 rounded-xl bg-white dark:bg-slate-800/60 cursor-pointer flex items-center justify-between hover:border-pink-400 focus:border-pink-500 transition-all"
        >
          <div className="min-w-0 flex-1 pr-3">
            {selectedCustomers.length === 0 ? (
              <span className="text-gray-400 text-sm truncate">Select Customers</span>
            ) : (
              <span className="block truncate text-sm font-medium text-pink-700 dark:text-pink-200">
                {selectedCustomers[0]}{selectedCustomers.length > 1 ? ` +${selectedCustomers.length - 1} more` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 pl-2">
            {selectedCustomers.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCustomersForActiveTab([]);
                }}
                className="w-7 h-7 rounded-full bg-pink-100 dark:bg-pink-900/60 text-pink-700 dark:text-pink-200 hover:bg-pink-200 dark:hover:bg-pink-800/70 inline-flex items-center justify-center"
                aria-label="Clear customer filter"
              >
                <X size={14} />
              </button>
            )}
            <div className="inline-flex items-center justify-center">
              <ChevronDown size={16} className="text-slate-400" />
            </div>
          </div>
        </div>
        {showCustomerDropdown && (
          <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-48 sm:max-h-64 overflow-y-auto">
            {customers.map((customer) => (
              <div
                key={customer.name}
                onClick={() => handleCustomerToggle(customer.name)}
                className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2 sm:gap-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedCustomers.includes(customer.name)}
                  onChange={() => {}}
                  className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500 flex-shrink-0"
                />
                <span className="text-gray-900 dark:text-white font-medium text-xs sm:text-sm truncate">
                  {customer.name} ({customer.count})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderCountryFilterControl = () => (
    <div className="relative country-dropdown-container group">
      <label className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">
        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
        Country
      </label>
      <div className="relative">
        <div
          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
          className="w-full h-10 px-3.5 py-0 text-[13px] border border-slate-200 dark:border-slate-700/70 rounded-xl bg-white dark:bg-slate-800/60 cursor-pointer flex items-center justify-between hover:border-green-400 transition-all"
        >
          <div className="min-w-0 flex-1 pr-3 flex items-center gap-2">
            {selectedCountry ? (
              <>
                <span className="text-base leading-none">{countryToFlag(selectedCountry)}</span>
                <span className="truncate text-sm font-medium text-green-700 dark:text-green-200">{selectedCountry}</span>
              </>
            ) : (
              <span className="text-gray-400 text-sm">All Countries</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 pl-2">
            {selectedCountry && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSelectedCountry(''); setShowCountryDropdown(false); }}
                className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/60 text-green-700 dark:text-green-200 hover:bg-green-200 inline-flex items-center justify-center"
              >
                <X size={14} />
              </button>
            )}
            <div className="inline-flex items-center justify-center">
              <ChevronDown size={16} className="text-slate-400" />
            </div>
          </div>
        </div>
        {showCountryDropdown && (
          <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-48 sm:max-h-64 overflow-y-auto">
            <div
              onClick={() => { setSelectedCountry(''); setShowCountryDropdown(false); }}
              className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2 sm:gap-3 border-b border-gray-200 dark:border-gray-700"
            >
              <span className="text-base">🌍</span>
              <span className="text-gray-900 dark:text-white font-medium text-xs sm:text-sm">All Countries</span>
              {!selectedCountry && <span className="ml-auto text-green-500 text-xs">✓</span>}
            </div>
            {countries.map((country) => (
              <div
                key={country}
                onClick={() => { setSelectedCountry(country); setShowCountryDropdown(false); gaCountryFilter(country, activeTab); }}
                className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2 sm:gap-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
              >
                <span className="text-base leading-none flex-shrink-0">{countryToFlag(country)}</span>
                <span className="text-gray-900 dark:text-white font-medium text-xs sm:text-sm truncate">{country}</span>
                {selectedCountry === country && <span className="ml-auto text-green-500 text-xs flex-shrink-0">✓</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const shellShipments = activeTab === 'Sea-Export' ? seaExportShipments : shipments;

  // What the header search offers while typing — the same list it filters, so a
  // suggestion can never point at a shipment the page would then hide.
  const searchSuggestions = globalSearch.trim()
    ? (activeTab === 'Sea-Export' ? filteredSeaShipments : filteredShipments).slice(0, 8).map((sh) => ({
        id: sh.id ?? sh.awbNo ?? sh.blNumber,
        title: sh.awbNo || sh.blNumber || '-',
        subtitle: [sh.customer || sh.consigneeName, sh.sbNo && `S/B ${sh.sbNo}`].filter(Boolean).join(' · '),
        meta: [sh.origin, sh.destination || sh.portOfDischargeCode].filter(Boolean).join(' → '),
      }))
    : [];


  return (
    <UserShell
      activeTab={activeTab}
      onTabChange={(tab) => { setActiveTab(tab); gaTabSwitch(tab); }}
      search={globalSearch}
      onSearch={setGlobalSearch}
      suggestions={searchSuggestions}
      onPickSuggestion={(sg) => setGlobalSearch(sg.title)}
    >
      <div className="max-w-[1500px] mx-auto">
        <div className="mb-5">
          <UserStatCards shipments={shellShipments} />
        </div>

        {/* Coming Soon for other tabs */}
        {activeTab !== 'Air-Export' && activeTab !== 'Sea-Export' ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="mb-6">
                {activeTab === 'Air-Import' && <Plane size={80} className="mx-auto text-blue-500 dark:text-blue-400" />}
                {activeTab === 'Sea-Import' && <Ship size={80} className="mx-auto text-teal-500 dark:text-teal-400" />}
              </div>
              <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-4">
                Coming Soon
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {activeTab} feature is under development
              </p>
            </div>
          </div>
        ) : activeTab === 'Sea-Export' ? (
          <>
            {/* Sea Export Content - Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
              <ActionCard
                icon={Calendar} tone="blue"
                title="Daily Insights" note="View your daily analytics"
                onClick={() => setFobCalendarType('sea')}
              />
              <ActionCard
                icon={BarChart3} tone="emerald"
                title="Deep Insights" note="Explore detailed analytics"
                onClick={() => { gaInsightsOpen(); navigate('/user/insights', { state: { transportTab: 'sea' } }); }}
              />
              <ActionCard
                icon={Download} tone="violet"
                title="CSV" note="Download your data"
                onClick={handleExportCSV}
              />
              <ActionCard
                icon={Plus} filled
                title="New Booking" note="Raise a new shipment"
                onClick={handleNewBooking}
              />
            </div>

            {/* Filters Section */}
            <div className="bg-white dark:bg-[#111a2e] rounded-2xl border border-slate-200/80 dark:border-slate-700/50 p-4 mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div className="group">
                  <label className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    B/L Number
                  </label>
                  <input
                    type="text"
                    value={searchAWB}
                    onChange={(e) => { setSearchAWB(e.target.value); if (e.target.value.length >= 3) gaAWBSearch(e.target.value, activeTab); }}
                    placeholder="Search by B/L..."
                    className="w-full h-10 px-3.5 text-[13px] rounded-xl bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/70 text-slate-700 dark:text-slate-200 placeholder-slate-400 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition"
                  />
                </div>
                {renderCustomerFilterControl()}
                {renderCountryFilterControl()}
                <div className="relative date-picker-container group">
                  <label className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">
                    <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                    Date Range
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formatDateRange()}
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      readOnly
                      className="w-full h-10 px-3.5 pr-10 text-[13px] rounded-xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-800/60 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer transition-all placeholder:text-gray-400"
                      placeholder="Select date range"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={15} />
                    {showDatePicker && (
                      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" onClick={() => setShowDatePicker(false)}>
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden w-full max-w-fit max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                          <div className="overflow-auto flex-1">
                            <div className="hidden sm:block">
                              <DateRangePicker
                                ranges={dateRange || [{
                                  startDate: new Date(),
                                  endDate: new Date(),
                                  key: 'selection'
                                }]}
                                onChange={(item) => setDateRange([item.selection])}
                                months={2}
                                direction="horizontal"
                                showSelectionPreview={true}
                                moveRangeOnFirstSelection={false}
                                minDate={new Date(2020, 0, 1)}
                                maxDate={new Date()}
                                rangeColors={['#a855f7']}
                              />
                            </div>
                            <div className="block sm:hidden">
                              <DateRangePicker
                                ranges={dateRange || [{
                                  startDate: new Date(),
                                  endDate: new Date(),
                                  key: 'selection'
                                }]}
                                onChange={(item) => setDateRange([item.selection])}
                                months={1}
                                direction="vertical"
                                showSelectionPreview={true}
                                moveRangeOnFirstSelection={false}
                                minDate={new Date(2020, 0, 1)}
                                maxDate={new Date()}
                                rangeColors={['#a855f7']}
                              />
                            </div>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-between gap-2 flex-shrink-0">
                            <button
                              onClick={() => {
                                setDateRange(null);
                                setShowDatePicker(false);
                              }}
                              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition text-sm"
                            >
                              Clear
                            </button>
                            <button
                              onClick={() => {
                                setShowDatePicker(false);
                                if (dateRange && dateRange[0]) {
                                  gaDateFilter(
                                    dateRange[0].startDate?.toISOString().split('T')[0],
                                    dateRange[0].endDate?.toISOString().split('T')[0],
                                    activeTab
                                  );
                                }
                              }}
                              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl font-medium transition text-sm shadow-lg"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="group flex items-end">
                  <button
                    onClick={resetFilters}
                    className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-bold inline-flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-sm whitespace-nowrap"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <p className="text-gray-700 dark:text-gray-300 font-semibold text-sm">
                  Total Shipments: {sortedFilteredSeaShipments.length}
                </p>
              </div>
            </div>

            {/* Sea Export Shipments Cards */}
            <div className="space-y-6">
              {loadingSeaExport ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading sea export shipments...</p>
                  </div>
                </div>
              ) : sortedFilteredSeaShipments.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-12 text-center">
                  <Ship size={64} className="mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                    No sea export shipments found
                  </p>
                </div>
              ) : (
                sortedFilteredSeaShipments.map((shipment, index) => {
                  const seaInvData = invoiceMap[normalizeKey(shipment.blNumber)];
                  const seaHasInvoice = !!shipment.invoicePdf;
                  const seaIsSelected = selectedForPayment.has(shipment.blNumber);
                  return (
                  <ShipmentCard
                    key={shipment.id}
                    isSea
                    shipment={{
                      ...shipment,
                      origin: shipment.portOfLoadingCode || shipment.origin,
                      originCity: shipment.portOfLoading || shipment.originCity,
                      dest: shipment.portOfDischargeCode,
                      destCity: shipment.portOfDischarge,
                      destCountry: shipment.countryOfFinalDestination,
                    }}
                    flag={countryToFlag}
                    selectable={seaHasInvoice}
                    selected={seaIsSelected}
                    onSelect={() => togglePaymentSelect(shipment.blNumber, shipment, seaInvData)}
                    onViewDetails={() => handleTrackAWB(shipment.blNumber)}
                    fields={[
                      { label: 'B/L Number', value: shipment.blNumber },
                      { label: 'S/B No.', value: shipment.sbNo },
                      { label: 'S/B Date', value: shipment.sbDate },
                      { label: 'Consignee', value: shipment.consigneeName },
                      { label: 'Buyer', value: shipment.buyerName },
                      { label: 'Total Pkt', value: shipment.pkg && shipment.pkg !== '-' ? `${shipment.pkg} Pkt` : '-' },
                      { label: 'FOB Value', value: shipment.fobValue },
                      { label: 'FC Value', value: formatFcValueWithCurrency(shipment.fcValue, shipment.currency) },
                      { label: 'Gr. Weight', value: shipment.gwt && shipment.gwt !== '-' ? `${shipment.gwt} Kg` : '-' },
                      { label: 'Container No', value: shipment.containerNo },
                      { label: 'Container Qty', value: shipment.containerQty },
                      { label: 'LEO Date', value: shipment.leoDate },
                    ]}
                    documents={[
                      hasPdfData(shipment.blPdf)      && { label: 'B/L',     tone: 'blue',   onClick: () => handleDownloadPDF(shipment, 'BL') },
                      hasPdfData(shipment.leoPdf)     && { label: 'LEO',     tone: 'purple', onClick: () => handleDownloadPDF(shipment, 'LEO') },
                      hasPdfData(shipment.invPlPdf)   && { label: 'INV/PL',  tone: 'pink',   onClick: () => handleDownloadPDF(shipment, 'INV/PL') },
                      hasPdfData(shipment.invoicePdf) && { label: 'INVOICE', tone: 'green',  onClick: () => handleDownloadPDF(shipment, 'INVOICE') },
                    ].filter(Boolean)}
                    payment={seaHasInvoice ? {
                      paid: shipment.paymentDetails?.shipment_payment_status === 'paid',
                      onPay: () => handlePayNow(shipment, seaInvData),
                    } : null}
                  />
                );})
              )}
              {/* Sea Export — infinite scroll sentinel */}
              <div ref={seaSentinelRef} className="h-4" />
              {seaLoadingMore && (
                <div className="flex items-center justify-center py-6 gap-3">
                  <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">Loading more shipments…</span>
                </div>
              )}
              {!seaHasMore && seaExportShipments.length > 0 && (
                <p className="text-center text-xs text-gray-400 dark:text-gray-600 py-4">
                  All {seaExportShipments.length} shipments loaded
                </p>
              )}
            </div>
          </>
        ) : (
<>
            {/* Air Export Content - Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
              <ActionCard
                icon={Calendar} tone="blue"
                title="Daily Insights" note="View your daily analytics"
                onClick={() => setFobCalendarType('air')}
              />
              <ActionCard
                icon={BarChart3} tone="emerald"
                title="Deep Insights" note="Explore detailed analytics"
                onClick={() => { gaInsightsOpen(); navigate('/user/insights', { state: { transportTab: 'air' } }); }}
              />
              <ActionCard
                icon={Download} tone="violet"
                title="CSV" note="Download your data"
                onClick={handleExportCSV}
              />
              <ActionCard
                icon={Plus} filled
                title="New Booking" note="Raise a new shipment"
                onClick={handleNewBooking}
              />
            </div>

            {/* Only once rows are picked, so it keeps a line of its own. */}
            {selectedForPayment.size > 0 && (
              <button type="button" onClick={handleBulkPay} className={`${PAY_BTN} mb-5`}>
                <CreditCard size={15} /> Bulk Pay ({selectedForPayment.size} selected)
                <span className="ml-1 px-2 py-0.5 rounded-md bg-white/20 text-[11px]">
                  ₹{(selectedTotal / 100000).toFixed(2)}L
                </span>
              </button>
            )}

            {/* Filters Section */}
            <div className="bg-white dark:bg-[#111a2e] rounded-2xl border border-slate-200/80 dark:border-slate-700/50 p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <div className="group">
              <label className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                AWB Number
              </label>
              <input
                type="text"
                value={searchAWB}
                onChange={(e) => { setSearchAWB(e.target.value); if (e.target.value.length >= 3) gaAWBSearch(e.target.value, activeTab); }}
                placeholder="Search by AWB..."
                className="w-full h-10 px-3.5 text-[13px] rounded-xl bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/70 text-slate-700 dark:text-slate-200 placeholder-slate-400 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition"
              />
            </div>
            {renderCustomerFilterControl()}
            {renderCountryFilterControl()}
            <div className="relative date-picker-container group">
              <label className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">
                <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                Date Range
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatDateRange()}
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  readOnly
                  className="w-full h-10 px-3.5 pr-10 text-[13px] rounded-xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-800/60 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer transition-all placeholder:text-gray-400"
                  placeholder="Select date range"
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={15} />
                {showDatePicker && (
                  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" onClick={() => setShowDatePicker(false)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden w-full max-w-fit max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                      <div className="overflow-auto flex-1">
                        <div className="hidden sm:block">
                          <DateRangePicker
                            ranges={dateRange || [{
                              startDate: new Date(),
                              endDate: new Date(),
                              key: 'selection'
                            }]}
                            onChange={(item) => setDateRange([item.selection])}
                            months={2}
                            direction="horizontal"
                            showSelectionPreview={true}
                            moveRangeOnFirstSelection={false}
                            minDate={new Date(2020, 0, 1)}
                            maxDate={new Date()}
                            rangeColors={['#a855f7']}
                          />
                        </div>
                        <div className="block sm:hidden">
                          <DateRangePicker
                            ranges={dateRange || [{
                              startDate: new Date(),
                              endDate: new Date(),
                              key: 'selection'
                            }]}
                            onChange={(item) => setDateRange([item.selection])}
                            months={1}
                            direction="vertical"
                            showSelectionPreview={true}
                            moveRangeOnFirstSelection={false}
                            minDate={new Date(2020, 0, 1)}
                            maxDate={new Date()}
                            rangeColors={['#a855f7']}
                          />
                        </div>
                      </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-between gap-2 flex-shrink-0">
                      <button
                        onClick={() => {
                          setDateRange(null);
                          setShowDatePicker(false);
                        }}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition text-sm"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          setShowDatePicker(false);
                          if (dateRange && dateRange[0]) {
                            gaDateFilter(
                              dateRange[0].startDate?.toISOString().split('T')[0],
                              dateRange[0].endDate?.toISOString().split('T')[0],
                              activeTab
                            );
                          }
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl font-medium transition text-sm shadow-lg"
                      >
                        Apply
                      </button>
                    </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="group flex items-end">
              <button
                onClick={resetFilters}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-bold inline-flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-sm whitespace-nowrap"
              >
                Reset Filters
              </button>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <p className="text-gray-700 dark:text-gray-300 font-semibold text-sm">
              Total Shipments: {sortedFilteredShipments.length}
            </p>
          </div>
        </div>

        {payableAir.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-5 rounded-2xl px-4 py-3
                          bg-white dark:bg-[#111a2e] border border-slate-200/80 dark:border-slate-700/50">
            <input
              type="checkbox"
              checked={allAirSelected}
              onChange={toggleSelectAllAir}
              className="w-[18px] h-[18px] cursor-pointer"
              style={{ accentColor: '#2563eb' }}
              aria-label="Select all payable shipments"
            />
            <span className="text-[13px] font-bold text-slate-800 dark:text-slate-100">
              Select All ({payableAir.length} Shipment{payableAir.length > 1 ? 's' : ''})
            </span>
            <span className="px-2.5 py-1 rounded-lg text-[11.5px] font-bold
                             bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
              {payableAir.length} Ready
            </span>
            {selectedForPayment.size > 0 && (
              <span className="text-[13px] text-slate-500 dark:text-slate-400">
                Total Payable:{' '}
                <b className="text-slate-800 dark:text-slate-100">₹{selectedTotal.toLocaleString('en-IN')}</b>
              </span>
            )}
            <div className="flex-1" />
            {selectedForPayment.size > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setSelectedForPayment(new Map())}
                  className="text-[13px] font-semibold text-slate-500 dark:text-slate-400 hover:underline"
                >
                  Deselect All
                </button>
                <button type="button" onClick={handleBulkPay} className={PAY_BTN}>
                  <CreditCard size={15} /> Proceed to Bulk Payment
                </button>
              </>
            )}
          </div>
        )}

        {/* Shipments Cards */}
        <div className="space-y-6">
          {loading ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading shipments...</p>
              </div>
            </div>
          ) : filteredShipments.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-12 text-center">
              <Package size={64} className="mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                No shipments found
              </p>
            </div>
          ) : (
            sortedFilteredShipments.map((shipment, index) => {
              const airInvData = invoiceMap[normalizeKey(shipment.awbNo)];
              const airHasInvoice = hasPdfData(shipment.invoicePdf);
              const airIsSelected = selectedForPayment.has(shipment.awbNo);
              return (
              <ShipmentCard
                key={shipment.id}
                shipment={{ ...shipment, dest: shipment.destination }}
                flag={countryToFlag}
                selectable={airHasInvoice}
                selected={airIsSelected}
                onSelect={() => togglePaymentSelect(shipment.awbNo, shipment, airInvData)}
                onViewDetails={() => handleTrackAWB(shipment.awbNo)}
                onTrack={() => {
                  const prefix = String(shipment.awbNo || '').replace(/\D/g, '').slice(0, 3);
                  if (liveTrackingPrefixes.has(prefix)) setTrackingAwb(shipment);
                  else handleTrackAWB(shipment.awbNo);
                }}
                fields={[
                  { label: 'AWB No.', value: shipment.awbNo },
                  { label: 'S/B No.', value: shipment.sbNo },
                  { label: 'S/B Date', value: shipment.sbDate },
                  { label: 'Consignee', value: shipment.customer },
                  { label: 'Buyer Name', value: shipment.buyerName || '-' },
                  { label: 'Total Pkt', value: shipment.totalPkt && shipment.totalPkt !== '-' ? `${shipment.totalPkt} Pkt` : '-' },
                  { label: 'FOB Value', value: shipment.fobValue },
                  { label: 'FC Value', value: formatFcValueWithCurrency(shipment.fcValue, shipment.fcType) },
                  { label: 'Gr. Weight', value: shipment.grWeight && shipment.grWeight !== '-' ? `${shipment.grWeight} Kg` : '-' },
                  { label: 'Ch. Weight', value: shipment.chWeight && shipment.chWeight !== '-' ? `${shipment.chWeight} Kg` : '-' },
                  { label: 'Flight No.', value: shipment.flightNo || '-' },
                  { label: 'Flight Date', value: shipment.flightDate || '-' },
                ]}
                documents={[
                  hasPdfData(shipment.awbPdf)     && { label: 'AWB',     tone: 'blue',   onClick: () => handleDownloadPDF(shipment, 'AWB Copy') },
                  hasPdfData(shipment.leoPdf)     && { label: 'LEO',     tone: 'purple', onClick: () => handleDownloadPDF(shipment, 'LEO') },
                  hasPdfData(shipment.invPdf)     && { label: 'INV/PL',  tone: 'pink',   onClick: () => handleDownloadPDF(shipment, 'INV/PL') },
                  hasPdfData(shipment.invoicePdf) && { label: 'INVOICE', tone: 'green',  onClick: () => handleDownloadPDF(shipment, 'INVOICE') },
                ].filter(Boolean)}
                payment={airHasInvoice ? {
                  paid: shipment.paymentDetails?.shipment_payment_status === 'paid',
                  onPay: () => handlePayNow(shipment, airInvData),
                } : null}
              />
            );})
          )}
          {/* Air Export — infinite scroll sentinel */}
          <div ref={airSentinelRef} className="h-4" />
          {airLoadingMore && (
            <div className="flex items-center justify-center py-6 gap-3">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading more shipments…</span>
            </div>
          )}
          {!airHasMore && shipments.length > 0 && (
            <p className="text-center text-xs text-gray-400 dark:text-gray-600 py-4">
              All {shipments.length} shipments loaded
            </p>
          )}
        </div>
          </>
        )}

      {/* Wallet Payment Modal */}
      {walletModal && (
        <WalletPayModal
          totalAmount={walletModal.totalAmount}
          walletBalance={balanceRupees}
          onClose={() => setWalletModal(null)}
          onPayWallet={() => payViaWallet(walletModal.payload, walletModal.totalAmount)}
          onPaySplit={(walletAmt) => openSplitPay(walletModal.payload, walletModal.totalAmount, walletAmt)}
          onPayRazorpay={() => { setWalletModal(null); openRazorpay(walletModal.payload); }}
        />
      )}

      {/* Live AWB Tracking Modal */}
      {trackingAwb && (
        <AwbTrackingModal
          awbNumber={trackingAwb.awbNo}
          onClose={() => setTrackingAwb(null)}
          onFallback={() => handleTrackAWB(trackingAwb.awbNo)}
        />
      )}

      {/* FOB calendar (day-wise FOB by S/B date) */}
      {fobCalendarType && (
        <FobCalendarModal
          userId={user?.id}
          transportType={fobCalendarType}
          onClose={closeFobCalendar}
        />
      )}


      </div>
      
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        
        .animate-float {
          animation: float 2s ease-in-out infinite;
        }
        
        @media (min-width: 1024px) {
          .details-grid {
            grid-template-columns: 1.3fr 1.3fr 1.3fr 0.9fr;
          }
        }
        @keyframes shine {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(250%) skewX(-15deg); }
        }
        .btn-shine {
          position: relative;
          overflow: hidden;
        }
        .btn-shine::after {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%);
          animation: shine 2s ease-in-out infinite;
        }
      `}</style>
    </UserShell>
  );
};

export default CustomerDashboard;
